import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import React from 'react';

import { LivePreviewEligibility, PreviewMode, SourceMode } from '../../types/previewStudio';

interface TopBarProps {
  breadcrumb: {
    client?: string;
    io?: string;
    campaign?: string;
    creative?: string;
  } | null;
  statusPills: Array<{
    label: string;
    color: 'success' | 'error' | 'warning' | 'info' | 'default';
  }>;
  mode: PreviewMode;
  onModeChange: (mode: PreviewMode) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  lastGeneratedTime: Date | null;
  autoRefresh: boolean;
  onAutoRefreshChange: (checked: boolean) => void;
  disabled?: boolean;
  isQAMode?: boolean; // Show QA Mode badge when using mock data
  liveEligibility?: LivePreviewEligibility | null;
  checkingEligibility?: boolean;
  onRefreshEligibility?: () => void;
  sourceMode?: SourceMode; // Used to disable live toggle when Creative Sandbox is selected
}

export default function TopBar({
  breadcrumb,
  statusPills,
  mode,
  onModeChange,
  onGenerate,
  isGenerating,
  lastGeneratedTime,
  autoRefresh,
  onAutoRefreshChange,
  disabled = false,
  isQAMode = false,
  liveEligibility,
  checkingEligibility = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onRefreshEligibility,
  sourceMode = 'campaign',
}: TopBarProps) {
  const formatTime = (date: Date | null) => {
    if (!date) return '--:--:-- --';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Helper functions to avoid nested ternaries
  const getButtonIcon = () => {
    if (isGenerating) return <CircularProgress size={16} color="inherit" />;
    if (lastGeneratedTime) return <RefreshIcon />;
    return <PlayArrowIcon />;
  };

  const getButtonText = () => {
    if (isGenerating) return 'Generating...';
    if (lastGeneratedTime) return 'Regenerate';
    return 'Generate Preview';
  };

  const getLiveChipLabel = () => {
    if (checkingEligibility) return 'Checking...';
    if (!liveEligibility) return 'Live: Unknown';
    return liveEligibility.eligible ? 'Live: Ready' : 'Live: Not Ready';
  };

  const getLiveChipColor = (): 'default' | 'success' | 'error' => {
    if (checkingEligibility) return 'default';
    if (!liveEligibility) return 'default';
    return liveEligibility.eligible ? 'success' : 'error';
  };

  const getLiveChipIcon = () => {
    if (checkingEligibility) return <CircularProgress size={12} />;
    if (!liveEligibility) return undefined;
    if (liveEligibility.eligible) return <CheckCircleIcon sx={{ fontSize: 14 }} />;
    return <ErrorIcon sx={{ fontSize: 14 }} />;
  };

  return (
    <AppBar
      position="static"
      color="default"
      elevation={1}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ gap: 2, minHeight: { xs: 56, sm: 64 }, position: 'relative', justifyContent: 'space-between' }}>
        {/* Left: Generate Button & Settings */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {/* Generate Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={onGenerate}
            disabled={disabled || isGenerating}
            startIcon={getButtonIcon()}
            sx={{
              minWidth: 140,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {getButtonText()}
          </Button>

          {/* Timestamp */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              Last generated
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
              {formatTime(lastGeneratedTime)}
            </Typography>
          </Box>

          {/* Auto-refresh */}
          <FormControlLabel
            control={
              <Checkbox
                checked={mode === 'live' ? false : autoRefresh}
                onChange={mode === 'live' ? undefined : (e) => onAutoRefreshChange(e.target.checked)}
                disabled={mode === 'live'}
                size="small"
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  opacity: mode === 'live' ? 0.5 : 1,
                }}
              >
                Auto-refresh
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </Box>

        {/* Center: Mode Toggle - Absolutely Centered */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode !== null) {
                onModeChange(newMode);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 0.5,
                fontSize: '0.75rem',
                textTransform: 'none',
              },
            }}
          >
            <ToggleButton value="simulated">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Simulated
                <Typography
                  component="span"
                  sx={{ fontSize: '0.65rem', opacity: 0.7, fontWeight: 400 }}
                >
                  (Fast)
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="live" disabled={sourceMode === 'raw_tag'}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Live
                <Typography
                  component="span"
                  sx={{ fontSize: '0.65rem', opacity: 0.7, fontWeight: 400 }}
                >
                  (QA)
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Right: Breadcrumb & Status */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 0.5, 
            minWidth: 0, 
            alignItems: 'flex-end',
            maxWidth: 'calc(50% - 120px)', // Prevent overlap with centered toggle (increased margin)
            pr: 1,
            flexShrink: 0,
          }}
        >
          {/* Breadcrumb */}
          {breadcrumb && (
            <Breadcrumbs
              separator="›"
              maxItems={4}
              itemsAfterCollapse={1}
              sx={{
                fontSize: '0.75rem',
                width: '100%',
                justifyContent: 'flex-end',
                '& .MuiBreadcrumbs-separator': {
                  mx: 0.5,
                },
                '& .MuiBreadcrumbs-ol': {
                  flexWrap: 'nowrap',
                },
              }}
            >
              {breadcrumb.client && (
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: 60, sm: 70, md: 80 },
                    display: 'block',
                  }}
                  title={breadcrumb.client}
                >
                  {breadcrumb.client}
                </Typography>
              )}
              {breadcrumb.io && (
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: 60, sm: 70, md: 80 },
                    display: 'block',
                  }}
                  title={breadcrumb.io}
                >
                  {breadcrumb.io}
                </Typography>
              )}
              {breadcrumb.campaign && (
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: 70, sm: 85, md: 100 },
                    display: 'block',
                  }}
                  title={breadcrumb.campaign}
                >
                  {breadcrumb.campaign}
                </Typography>
              )}
              {breadcrumb.creative && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.primary',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: 80, sm: 100, md: 120 },
                    display: 'block',
                  }}
                  title={breadcrumb.creative}
                >
                  {breadcrumb.creative}
                </Typography>
              )}
            </Breadcrumbs>
          )}
          {/* Status Pills */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {isQAMode && (
              <Chip
                label="QA Mode"
                size="small"
                color="warning"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
              />
            )}
            {statusPills.map((pill, index) => (
              <Chip
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                label={pill.label}
                size="small"
                color={pill.color}
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
              />
            ))}
            {/* Live Preview Status Chip */}
            {mode === 'live' && (
              <Chip
                label={getLiveChipLabel()}
                size="small"
                color={getLiveChipColor()}
                icon={getLiveChipIcon()}
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

